import { useState } from "react";
import { Phone, MessageCircle, MapPin, Clock, Mail, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/919891315777?text=Hi%2C%20I%20want%20to%20book%20a%20water%20tank%20cleaning%20service%20in%20Gurugram";

const serviceOptions = [
  "Residential Tank Cleaning",
  "Commercial Tank Cleaning",
  "Hotel & Hospitality Cleaning",
  "Overhead Tank Cleaning",
  "Underground Tank Cleaning",
];

export default function ContactBooking() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: "",
    date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSubmitted(true);
      toast.success("Booking submitted successfully!");
      const whatsappText = `Hi, I want to book ${form.service}. Name: ${form.name}, Phone: ${form.phone}${form.date ? `, Date: ${form.date}` : ""}`;
      window.open(
        `https://wa.me/919891315777?text=${encodeURIComponent(whatsappText)}`,
        "_blank"
      );
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", phone: "", service: "", date: "", message: "" });
    setSubmitted(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <p className="text-sky font-medium text-sm uppercase tracking-[0.2em] mb-3">
            Get In Touch
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-navy">
            Book Your Cleaning
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="space-y-6">
              <a
                href="tel:+919891315777"
                data-testid="contact-phone-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-xl bg-sky/10 flex items-center justify-center group-hover:bg-sky/20 transition-colors">
                  <Phone className="w-5 h-5 text-sky" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Call Us</p>
                  <p className="font-heading font-semibold text-navy text-lg">
                    +91 98913 15777
                  </p>
                </div>
              </a>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-xl bg-whatsapp/10 flex items-center justify-center group-hover:bg-whatsapp/20 transition-colors">
                  <MessageCircle className="w-5 h-5 text-whatsapp" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">WhatsApp</p>
                  <p className="font-heading font-semibold text-navy text-lg">
                    Chat with us
                  </p>
                </div>
              </a>

              <a
                href="mailto:info@tankbuddy.in"
                data-testid="contact-email-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-xl bg-sky/10 flex items-center justify-center group-hover:bg-sky/20 transition-colors">
                  <Mail className="w-5 h-5 text-sky" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-heading font-semibold text-navy text-lg">
                    info@tankbuddy.in
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-sky" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="font-heading font-semibold text-navy text-lg">
                    Gurugram, Haryana
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-sky" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Working Hours</p>
                  <p className="font-heading font-semibold text-navy text-lg">
                    Mon - Sun, 7 AM - 8 PM
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                data-testid="google-map-embed"
                title="TankBuddy Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d112173.07258288498!2d76.91369065!3d28.4594965!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d19d582e38859%3A0x2cf5fe8e5c64b1e!2sGurugram%2C%20Haryana!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div>
            {submitted ? (
              <div
                data-testid="booking-thank-you"
                className="bg-surface border border-slate-200 rounded-xl p-8 text-center space-y-5"
              >
                <div className="w-16 h-16 rounded-full bg-whatsapp/10 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-whatsapp" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-navy">
                  Thank You!
                </h3>
                <p className="text-slate-600 text-base leading-relaxed max-w-sm mx-auto">
                  Your booking request has been submitted successfully. Our team will contact you within 30 minutes to confirm your appointment.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                  <Button
                    asChild
                    className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6"
                  >
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Chat on WhatsApp
                    </a>
                  </Button>
                  <Button
                    onClick={resetForm}
                    variant="outline"
                    data-testid="booking-new-btn"
                    className="border-navy text-navy hover:bg-navy hover:text-white rounded-full px-6"
                  >
                    Submit Another Booking
                  </Button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="booking-form"
                className="bg-surface border border-slate-200 rounded-xl p-8 space-y-5"
              >
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-navy font-medium text-sm">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="booking-name-input"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-lg border-slate-200 focus:border-sky focus:ring-sky"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-navy font-medium text-sm">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    data-testid="booking-phone-input"
                    placeholder="+91 XXXXX XXXXX"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="rounded-lg border-slate-200 focus:border-sky focus:ring-sky"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service" className="text-navy font-medium text-sm">
                    Service Type *
                  </Label>
                  <Select
                    value={form.service}
                    onValueChange={(val) => setForm({ ...form, service: val })}
                  >
                    <SelectTrigger
                      data-testid="booking-service-select"
                      className="rounded-lg border-slate-200"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date" className="text-navy font-medium text-sm">
                    Preferred Date
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    data-testid="booking-date-input"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="rounded-lg border-slate-200 focus:border-sky focus:ring-sky"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-navy font-medium text-sm">
                    Additional Details
                  </Label>
                  <Input
                    id="message"
                    data-testid="booking-message-input"
                    placeholder="Tank size, location details, etc."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="rounded-lg border-slate-200 focus:border-sky focus:ring-sky"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="booking-submit-btn"
                  disabled={submitting}
                  className="w-full bg-sky hover:bg-sky/90 text-white rounded-full py-6 text-base font-semibold transition-all duration-300 hover:shadow-lg"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? "Submitting..." : "Submit & Book on WhatsApp"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
